import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { AccountSelect } from '@ovComponents/3-pattern/accountSelect/accountSelect';
import { Alert, Box, Button, Divider, Typography } from 'hephaestus/web';
import { Add as AddIcon } from 'hephaestus/webIcons';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { REDEEM_MODES } from '../calculator.constants';
import type { RedeemMode } from '../calculator.types';
import {
  getCalculatorLabel,
  isCalculatorFieldEnabled,
} from '../calculator.utils';
import type { CalculatorState } from '../useCalculator';
import { CalculatorFundCard } from './calculatorFundCard';

export const CalculatorInputPanel = ({
  calculator,
  forPrint = false,
  options,
}: {
  calculator: CalculatorState;
  forPrint?: boolean;
  options: Record<string, unknown>;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const {
    accountLoading,
    accountName,
    addFundLine,
    foreignExchangeRates,
    fundLines,
    handleCalculate,
    handleClear,
    holdings,
    isCalculating,
    isValid,
    redeemMode,
    removeFundLine,
    resultCurrency,
    selectedAccountId,
    setRedeemMode,
    setSelectedAccountId,
    showAccountSelect,
    showMultipleFundOption,
    updateFundLine,
    workflowUserId,
  } = calculator;

  const singleLine = fundLines[0];
  const redeemLabel = getCalculatorLabel(
    translateBackendString,
    options,
    'redeem',
    t('calculator.redeem'),
  );
  const hasNoSecurities =
    !accountLoading &&
    holdings.length === 0 &&
    (!showAccountSelect || !!selectedAccountId);

  return (
    <Box data-testid='calculator-input-panel'>
      <Box
        data-pdf-section
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}
      >
        <Typography variant='headingXSmall' sx={{ fontWeight: 700 }}>
          {translateBackendString(options.inputTitle) ||
            t('calculator.input.title')}
        </Typography>
        {showAccountSelect && (
          <Box>
            {forPrint ? (
              <TextField
                fullWidth
                label={t('calculator.selectAccount')}
                value={accountName}
                onChange={() => {}}
              />
            ) : (
              <AccountSelect
                forId={workflowUserId ?? ''}
                label={t('calculator.selectAccount')}
                value={selectedAccountId}
                onChange={(event: { target: { value: string } }) =>
                  setSelectedAccountId(event.target.value)
                }
                onBlur={() => {}}
                includeEmpty
                disabled={accountLoading}
                testId='calculator-account-select'
              />
            )}
          </Box>
        )}
        {hasNoSecurities && (
          <Box>
            <Alert severity='info' data-testid='calculator-no-securities-alert'>
              {t('calculator.noSecuritiesToRedeem')}
            </Alert>
          </Box>
        )}
        {isCalculatorFieldEnabled(options.redeem) && (
          <Box>
            <SelectField
              fullWidth
              label={redeemLabel}
              value={redeemMode}
              disabled={accountLoading || hasNoSecurities}
              onChange={(event: { target: { value: RedeemMode } }) =>
                setRedeemMode(event.target.value)
              }
              onBlur={() => {}}
              testId='calculator-redeem-mode'
            >
              <MenuItem value={REDEEM_MODES.SINGLE}>
                {t('calculator.redeemModes.single')}
              </MenuItem>
              {showMultipleFundOption && (
                <MenuItem value={REDEEM_MODES.MULTIPLE}>
                  {t('calculator.redeemModes.multiple')}
                </MenuItem>
              )}
              <MenuItem value={REDEEM_MODES.ENTIRE}>
                {t('calculator.redeemModes.entire')}
              </MenuItem>
            </SelectField>
          </Box>
        )}
        {redeemMode !== REDEEM_MODES.MULTIPLE &&
          redeemMode !== REDEEM_MODES.ENTIRE &&
          singleLine && (
            <CalculatorFundCard
              cardIndex={0}
              disabled={accountLoading || hasNoSecurities}
              foreignExchangeRates={foreignExchangeRates}
              fundLines={fundLines}
              holdings={holdings}
              line={singleLine}
              options={options}
              resultCurrency={resultCurrency}
              showCardChrome={false}
              onChange={(updates) => updateFundLine(0, updates)}
              onRemove={() => removeFundLine(0)}
            />
          )}
      </Box>
      {redeemMode === REDEEM_MODES.MULTIPLE &&
        fundLines.map((line, index) => (
          <Box
            key={`${line.fundCode}-${index}`}
            data-pdf-section
            sx={{ display: 'flex', flexDirection: 'column', pb: 1 }}
          >
            <CalculatorFundCard
              cardIndex={index}
              disabled={accountLoading || hasNoSecurities}
              foreignExchangeRates={foreignExchangeRates}
              fundLines={fundLines}
              holdings={holdings}
              line={line}
              multipleFundTitle={options.multipleFundTitle}
              options={options}
              resultCurrency={resultCurrency}
              onChange={(updates) => updateFundLine(index, updates)}
              onRemove={() => removeFundLine(index)}
            />
          </Box>
        ))}
      <Box
        data-pdf-section
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, pb: 1 }}
      >
        {redeemMode === REDEEM_MODES.MULTIPLE && (
          <Box sx={{ display: 'flex' }}>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={addFundLine}
              disabled={accountLoading || hasNoSecurities}
              data-testid='calculator-add-fund'
            >
              {translateBackendString(options.addFundButton) ||
                t('calculator.addFund')}
            </Button>
          </Box>
        )}
        <Divider />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            loading={isCalculating}
            onClick={handleCalculate}
            disabled={!isValid || accountLoading}
            data-testid='calculator-calculate-button'
          >
            {t('calculator.calculate')}
          </Button>
          <Button
            variant='text'
            onClick={handleClear}
            disabled={isCalculating}
            data-testid='calculator-clear-button'
          >
            {t('calculator.clear')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
